import { useEffect, useState, useCallback } from 'react';
import { Typography, Box } from '@mui/material';
import { ChangePasswordRequest, CustomerLoginInfo } from 'orderme-api-integration-client';
import { DecodedToken, DialogProps } from '../../../interfaces/interfaces';
import { CustomerClient } from '../../../helpers/client';
import { Popup } from '../../popup/popup';
import { useTranslation } from 'react-i18next';
import { PasswordRulesGrid } from './accountSettingsStyles';
import { authenticate, forceLoginAgain } from '../../../redux/reducers/authReducer';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { jwtDecode } from 'jwt-decode';
import PasswordTextField from './passwordTextField';
import { resolveErrorMessage } from '@/helpers/errorMessage';

interface FieldValues {
  currentPassword: string;
  newPassword: string;
  retypedPassword: string;
}

const initValue = {
  currentPassword: '',
  newPassword: '',
  retypedPassword: '',
} as FieldValues;

export function PasswordChange(props: DialogProps) {
  const dispatch = useAppDispatch();
  const { onClose, open } = props;
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const customersClient: CustomerClient = new CustomerClient();
  const [textFieldValues, setTextFieldValues] = useState<FieldValues>(initValue);
  const [success, setSuccess] = useState(false);
  const [failure, setFailure] = useState<string>(null);
  const passwordMatch = textFieldValues.newPassword === textFieldValues.retypedPassword;
  const { t } = useTranslation();
  const loginInfo: CustomerLoginInfo = useAppSelector((state: RootState) => state.customerState.loginInfo);
  const accessToken: string = useAppSelector((state: RootState) => state.authState.accessToken);

  const validate = useCallback(() => {
    Object.values(textFieldValues).every((value) => value !== '') && passwordMatch
      ? setIsButtonDisabled(false)
      : setIsButtonDisabled(true);
  }, [textFieldValues, passwordMatch]);

  useEffect(() => {
    validate();
  }, [validate]);

  useEffect(() => {
    setTextFieldValues(initValue);
  }, [open]);

  const changePassword = async () => {
    const changePasswordRequest = {
      currentPassword: textFieldValues.currentPassword,
      newPassword: textFieldValues.newPassword,
    } as ChangePasswordRequest;
    setSuccess(false);
    setFailure(null);

    try {
      await customersClient.changePassword(changePasswordRequest);
      setSuccess(true);
      closeAndLogout();
    } catch (err) {
      console.log(err);
      setFailure(resolveErrorMessage(err, t('account:changePasswordError')));
    }
  };

  const closeAndLogout = () => {
    setIsButtonDisabled(true);
    setTimeout(function () {
      if (loginInfo.phone) {
        dispatch(
          authenticate({
            input: loginInfo.phone,
            password: textFieldValues.newPassword,
          })
        );
      } else if (accessToken) {
        var decoded = jwtDecode(accessToken) as DecodedToken;
        dispatch(
          authenticate({
            input: decoded.nameid,
            password: textFieldValues.newPassword,
          })
        );
      } else {
        dispatch(forceLoginAgain());
      }

      onClose();
      setIsButtonDisabled(false);
      setSuccess(false);
      setFailure(null);
    }, 2000);
  };

  const handleFieldChange = (e: any) => {
    const { name, value } = e.target;
    const fieldValue = { [name]: value } as Pick<ChangePasswordRequest, keyof ChangePasswordRequest>;

    setTextFieldValues({
      ...textFieldValues,
      ...fieldValue,
    });
  };

  return (
    <Popup
      submitOnEnter
      onClose={() => onClose()}
      open={open}
      dialogTitle={t('account:changePassword')}
      dialogContent={
        <>
          <PasswordTextField
            fieldName="currentPassword"
            label={t('account:oldPassword')}
            onChange={handleFieldChange}
          />

          <PasswordTextField fieldName="newPassword" label={t('account:newPassword')} onChange={handleFieldChange} />

          <PasswordTextField
            fieldName="retypedPassword"
            label={t('account:retypeNewPassword')}
            onChange={handleFieldChange}
          />

          {textFieldValues.retypedPassword !== '' && textFieldValues.newPassword !== '' && !passwordMatch && (
            <Box sx={{ color: 'error.main', marginBottom: '10px' }}>{t('account:differentPasswords')}</Box>
          )}
          <PasswordRulesGrid>
            {(!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(textFieldValues.newPassword) ||
              textFieldValues.newPassword.length < 8) && <Typography>{t('account:passwordRules')}</Typography>}
            {textFieldValues.newPassword.length < 8 && <Typography>* {t('account:passwordRule8Chars')}</Typography>}
            {!/[A-Z]+/.test(textFieldValues.newPassword) && (
              <Typography>* {t('account:passwordRuleCapitalLetter')}</Typography>
            )}
            {!/[a-z]+/.test(textFieldValues.newPassword) && (
              <Typography>* {t('account:passwordRuleLowerCase')}</Typography>
            )}
            {!/[0-9]+/.test(textFieldValues.newPassword) && (
              <Typography>* {t('account:passwordRuleNumber')}</Typography>
            )}
          </PasswordRulesGrid>
        </>
      }
      onAction={changePassword}
      buttonContent={t('account:confirm')}
      isButtonDisabled={isButtonDisabled}
      isSuccess={success}
      successText={t('account:passwordChanged')}
      isFailure={failure !== null}
      failureText={failure}
    />
  );
}
